// "Maintenance and archive" on the project page, for a PROJECT MANAGER of a hosted project (a
// project admin taking part, or an admin: `can_archive`; tasks 2026-10-05 "a hosted database can
// be archived read-only" and 2026-10-12 "project admins archive and a portal admin can
// unarchive"; backend/unio/routers/archive.py).
//
//   open        -> Maintenance… (a reason for the banner) | Archive… (notes, the database
//                  name retyped): maintenance now, a final release, archived after
//   maintenance -> Open again (only a manual maintenance; a migration ends its own)
//   archived    -> admin only (`can_unarchive`): Un-archive… (name retyped: open again, the
//                  final release stays) | Move to cold storage… (name retyped: dumped, dropped)
//   cold        -> admin only: Restore from archive
// While a job runs (archiving / cooling / restoring) the panel polls GET .../archive. Everybody
// else sees only the banner (components/HostingModeBanner.jsx, in the hosted badge).
import { useEffect, useState } from "react";
import { api } from "../api";
import { ErrorNote, SectionTitle } from "../components/Feedback";
import { Btn, Card, ConfirmDialog, Field, Input, Spinner, Textarea } from "../components/Shared";
import { useApi } from "../useApi";

const RUNNING = ["archiving", "cooling", "restoring"];
const POLL_MS = 2000;
const P = { margin: "0 0 10px", fontSize: 14, color: "#374151" };

export function ArchiveDialog({ status, onConfirm, onClose, busy, error }) {
  const [notes, setNotes] = useState("");
  return (
    <ConfirmDialog title={`Archive ${status.database_name}?`} confirmWord={status.database_name}
                   confirmLabel="Archive" busy={busy}
                   onConfirm={() => onConfirm({ notes: notes || null, confirm: status.database_name })}
                   onClose={onClose}>
      <p style={P}>
        The database becomes <strong>read-only</strong>: data entry stops now, a final release (a dump,
        every table as CSV, the RO-Crate) is published, and then every write role, personal SQL login
        and write API token loses the right to change rows. Reading and the releases stay. Only a
        portal admin can un-archive it; the revoked write API tokens stay revoked.
      </p>
      <Field label="Notes" hint="Optional: why it is archived, e.g. the end of the project.">
        <Textarea value={notes} onChange={e => setNotes(e.target.value)} />
      </Field>
      <ErrorNote error={error} />
    </ConfirmDialog>
  );
}

export default function ArchivePanel({ projectId }) {
  const state = useApi(`/projects/${projectId}/archive`);
  const [dialog, setDialog] = useState(null);         // "archive" | "cold" | "unarchive" | null
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const d = state.data;
  const a = (d && d.archive) || {};
  const running = RUNNING.includes(a.state);
  const { reload } = state;

  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [running, reload]);

  if (state.error) return state.error.status === 404 ? null : <ErrorNote error={state.error} />;
  if (!d || !d.hosted || !d.can_archive) return null;

  const act = async (call, close = true) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      if (close) setDialog(null);
      reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const setMode = (mode) => act(() => api.put(`/projects/${projectId}/hosting/mode`, { mode, reason: reason || null }));
  const archive = body => act(() => api.post(`/projects/${projectId}/archive`, body));
  const cold = () => act(() => api.post(`/projects/${projectId}/archive/cold`, { confirm: d.database_name }));
  const restore = () => act(() => api.post(`/projects/${projectId}/archive/restore`, {}));
  const unarchive = () => act(() => api.post(`/projects/${projectId}/archive/open`, { confirm: d.database_name }));
  const open = what => () => { setError(null); setDialog(what); };

  return (
    <section aria-labelledby="archive-title" style={{ marginBottom: 24 }}>
      <SectionTitle><span id="archive-title">Maintenance and archive</span></SectionTitle>
      <Card>
        <p style={P}>
          {d.database_name} is <strong data-testid="hosting-mode">{running ? a.state : d.mode}</strong>
          {d.mode === "maintenance" && d.maintenance_reason ? `: ${d.maintenance_reason}` : ""}.
        </p>
        {running && <p role="status" style={P}><Spinner /> {a.state === "archiving" ? "The final release is being made…"
          : a.state === "cooling" ? "Dumping the database to S3, then dropping it…" : "Restoring the database from its dump…"}</p>}

        {!running && d.mode === "open" && (
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Field label="Reason shown in the banner" hint="Optional.">
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Moving to a new server" />
            </Field>
            <Btn variant="secondary" disabled={busy} onClick={() => setMode("maintenance")}>Put into maintenance</Btn>
            <Btn variant="danger" disabled={busy} onClick={open("archive")}>Archive…</Btn>
          </div>
        )}
        {!running && d.mode === "maintenance" && d.maintenance_reason && (
          <Btn disabled={busy} onClick={() => setMode("open")}>Open again</Btn>
        )}
        {!running && a.state === "archived" && d.can_unarchive && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Btn variant="secondary" disabled={busy} onClick={open("unarchive")}>Un-archive…</Btn>
            <Btn variant="secondary" disabled={busy} onClick={open("cold")}>Move to cold storage…</Btn>
          </div>
        )}
        {!running && a.state === "cold" && d.can_unarchive && (
          <Btn disabled={busy} onClick={restore}>Restore from archive</Btn>
        )}
        {!running && ["archived", "cold"].includes(a.state) && !d.can_unarchive && (
          <p style={P}>Only a portal admin can un-archive it{a.state === "cold" ? " or restore it from cold storage" : " or move it to cold storage"}.</p>
        )}
        {a.state === "unarchived" && (
          <p style={P}>
            Un-archived by {a.unarchived_by}{a.unarchived_at ? ` on ${a.unarchived_at.slice(0, 10)}` : ""}; release {a.release} stays
            published as its archived state.
          </p>
        )}
        {a.state === "failed" && <p style={{ ...P, color: "#92400e", marginTop: 10 }}>The last archiving failed; the database is {d.mode} again.</p>}
        {!dialog && <ErrorNote error={error} />}
        {a.log && (
          <details style={{ marginTop: 10 }}>
            <summary style={{ fontSize: 13, cursor: "pointer" }}>Archive log</summary>
            <pre style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, whiteSpace: "pre-wrap", margin: "6px 0 0" }}>{a.log}</pre>
          </details>
        )}
      </Card>
      {dialog === "archive" && (
        <ArchiveDialog status={d} busy={busy} error={error}
                       onConfirm={archive} onClose={() => setDialog(null)} />
      )}
      {dialog === "cold" && (
        <ConfirmDialog title={`Move ${d.database_name} to cold storage?`} confirmWord={d.database_name}
                       confirmLabel="Dump and drop" busy={busy} onConfirm={cold} onClose={() => setDialog(null)}>
          <p style={P}>
            The archived database is dumped to S3 and then dropped, freeing its server resources. Its
            releases stay downloadable; reading its tables needs a restore, which an admin starts here.
          </p>
          <ErrorNote error={error} />
        </ConfirmDialog>
      )}
      {dialog === "unarchive" && (
        <ConfirmDialog title={`Un-archive ${d.database_name}?`} confirmWord={d.database_name}
                       confirmLabel="Un-archive" busy={busy} onConfirm={unarchive} onClose={() => setDialog(null)}>
          <p style={P}>
            The database is open again: the data roles, personal SQL logins and the data forms can change rows.
            The final release {a.release} stays published and citable as its archived state. The write API
            tokens revoked at archiving stay revoked; their owners create new ones.
          </p>
          <ErrorNote error={error} />
        </ConfirmDialog>
      )}
    </section>
  );
}
